import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ecommerceService } from '../../services/ecommerceService';

// Async thunks
export const fetchProductReviews = createAsyncThunk(
  'ecommerceReviews/fetchProductReviews',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.getProductReviews(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createReview = createAsyncThunk(
  'ecommerceReviews/createReview',
  async (reviewData, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.createReview(reviewData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateReview = createAsyncThunk(
  'ecommerceReviews/updateReview',
  async ({ reviewId, data }, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.updateReview(reviewId, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteReview = createAsyncThunk(
  'ecommerceReviews/deleteReview',
  async (reviewId, { rejectWithValue }) => {
    try {
      await ecommerceService.deleteReview(reviewId);
      return reviewId;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const voteReview = createAsyncThunk(
  'ecommerceReviews/voteReview',
  async ({ reviewId, isHelpful }, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.voteReview(reviewId, isHelpful);
      return { reviewId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchFeaturedReviews = createAsyncThunk(
  'ecommerceReviews/fetchFeaturedReviews',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.getFeaturedReviews();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchRecentReviews = createAsyncThunk(
  'ecommerceReviews/fetchRecentReviews',
  async (limit = 10, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.getRecentReviews(limit);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  reviews: [],
  featuredReviews: [],
  recentReviews: [],
  reviewStats: null,
  pagination: {
    count: 0,
    next: null,
    previous: null,
  },
  loading: false,
  submitting: false,
  error: null,
  submitError: null,
  submitSuccess: false,
};

const ecommerceReviewSlice = createSlice({
  name: 'ecommerceReviews',
  initialState,
  reducers: {
    clearReviewError: (state) => {
      state.error = null;
      state.submitError = null;
    },
    clearSubmitStatus: (state) => {
      state.submitSuccess = false;
      state.submitError = null;
    },
    clearReviews: (state) => {
      state.reviews = [];
      state.pagination = initialState.pagination;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Product Reviews
      .addCase(fetchProductReviews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductReviews.fulfilled, (state, action) => {
        state.loading = false;
        state.reviews = action.payload.results || action.payload;
        state.reviewStats = action.payload.stats || null;
        state.pagination = {
          count: action.payload.count || 0,
          next: action.payload.next || null,
          previous: action.payload.previous || null,
        };
      })
      .addCase(fetchProductReviews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create Review
      .addCase(createReview.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
        state.submitSuccess = false;
      })
      .addCase(createReview.fulfilled, (state, action) => {
        state.submitting = false;
        state.submitSuccess = true;
        state.reviews.unshift(action.payload);
      })
      .addCase(createReview.rejected, (state, action) => {
        state.submitting = false;
        state.submitError = action.payload;
      })

      // Update Review
      .addCase(updateReview.fulfilled, (state, action) => {
        const index = state.reviews.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.reviews[index] = action.payload;
        }
      })

      // Delete Review
      .addCase(deleteReview.fulfilled, (state, action) => {
        state.reviews = state.reviews.filter(r => r.id !== action.payload);
      })

      // Vote Review
      .addCase(voteReview.fulfilled, (state, action) => {
        const index = state.reviews.findIndex(r => r.id === action.payload.reviewId);
        if (index !== -1) {
          state.reviews[index] = {
            ...state.reviews[index],
            ...action.payload.data,
          };
        }
      })

      // Featured Reviews
      .addCase(fetchFeaturedReviews.fulfilled, (state, action) => {
        state.featuredReviews = action.payload.results || action.payload;
      })

      // Recent Reviews
      .addCase(fetchRecentReviews.fulfilled, (state, action) => {
        state.recentReviews = action.payload.results || action.payload;
      });
  },
});

export const {
  clearReviewError,
  clearSubmitStatus,
  clearReviews,
} = ecommerceReviewSlice.actions;

export default ecommerceReviewSlice.reducer;
